import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import type { TabsProps } from "@mui/material/Tabs";
import Tabs from "@mui/material/Tabs";
import { theme } from "@polifonia/theme";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { EmailInfoTab } from "@/pages/channels/components/EmailInfoTab";
import { ChannelQueriesContextProviderById } from "@/pages/channels/pages/detail/channelQueriesContext";
import { DnsVerifyTab } from "@/pages/channels/pages/emailOnboard/components/DnsVerifyTab";
import { DomainSetupTab } from "@/pages/channels/pages/emailOnboard/components/DomainSetupTab";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const EMAIL_ONBOARD_TABS = [
  "domain-setup",
  "dns-verify",
  "email-info",
] as const;

type EmailOnboardTab = (typeof EMAIL_ONBOARD_TABS)[number];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = function TabPanel({
  children,
  value,
  index,
}) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`email-onboard-tabpanel-${index}`}
      aria-labelledby={`email-onboard-tab-${index}`}
    >
      {value === index ? <Box>{children}</Box> : null}
    </Box>
  );
};

function a11yProps(index: number) {
  return {
    id: `email-onboard-tab-${index}`,
    "aria-controls": `email-onboard-tabpanel-${index}`,
  };
}

export const EmailOnboardPageInternal: React.FC =
  function EmailOnboardPageInternal() {
    const { t } = useTranslation();

    // The onboarding page acts as a "new email channel" form by default;
    // we only attach to a specific channel once the user submits, so an org
    // with existing email channels still gets a fresh form to add another
    // sub-domain. Existing channels are managed from the /channels list,
    // not auto-loaded here.
    const channelsQuery = interlude.channel.useChannels();
    const [createdChannelId, setCreatedChannelId] = useState<number | null>(
      null,
    );
    const emailChannel = useMemo(() => {
      if (createdChannelId === null) return null;
      return (
        channelsQuery.data?.channels.find(
          (c) => c.type === "email" && c.id === createdChannelId,
        ) ?? null
      );
    }, [channelsQuery.data, createdChannelId]);

    const senderDomainsQuery = interlude.channel.useGetChannelSenderDomains(
      { params: { channelId: emailChannel?.id ?? 0 } },
      { enabled: Boolean(emailChannel?.id) },
    );
    const senderDomain = senderDomainsQuery.data?.[0] ?? null;

    // Sender profiles drive the read-only pre-fill of the domain-setup tab once
    // the channel exists (so the user can see what they entered while waiting
    // on DNS verification).
    const senderProfilesQuery = interlude.channel.useGetChannelSenderProfiles(
      { params: { channelId: emailChannel?.id ?? 0 } },
      { enabled: Boolean(emailChannel?.id) },
    );

    // Adapt the new entities to the shape the tab components expect.
    // `brandDomain` is FE-only and not persisted by the backend, so it is
    // intentionally omitted here.
    const application = useMemo(() => {
      if (!emailChannel || !senderDomain) return null;
      const verified = senderDomain.status === "verified";
      const senderProfiles = senderProfilesQuery.data ?? [];
      return {
        id: senderDomain.id,
        channelId: emailChannel.id,
        senderDomain: senderDomain.domain,
        dnsRecords: senderDomain.dnsRecords,
        // Per-record validation results live in these fields, not in
        // dnsRecords[].valid — DnsVerifyTab reads them to render each record's
        // verified/pending state.
        mailCnameStatus: senderDomain.mailCnameStatus,
        dkim1Status: senderDomain.dkim1Status,
        dkim2Status: senderDomain.dkim2Status,
        dmarcStatus: senderDomain.dmarcStatus,
        // Read-only pre-fill for the domain-setup tab. `emailLocal` is the part
        // before `@` in the persisted from-email. Only include `replyToEmail`
        // when present (exactOptionalPropertyTypes rejects an explicit
        // undefined).
        senderProfilesData: senderProfiles.map((p) => ({
          name: p.name,
          emailLocal: p.fromEmail.split("@")[0] ?? "",
        })),
        ...(senderProfiles[0]?.replyTo
          ? { replyToEmail: senderProfiles[0].replyTo }
          : {}),
        status: verified ? ("active" as const) : ("pending_dns" as const),
      };
    }, [emailChannel, senderDomain, senderProfilesQuery.data]);

    // Tabs follow the onboarding milestone — the user only sees what they
    // can act on right now:
    //  - Before verify: 寄件網域設定 + DNS 驗證 (Email 渠道資訊 is not yet meaningful)
    //  - After verify:  DNS 驗證 + Email 渠道資訊 (寄件網域設定 is locked-in)
    const isVerified = application?.status === "active";

    const visibleTabs = useMemo<ReadonlyArray<EmailOnboardTab>>(
      () =>
        isVerified
          ? (["dns-verify", "email-info"] as const)
          : (["domain-setup", "dns-verify"] as const),
      [isVerified],
    );

    const [selectedTab, setSelectedTab] = useState<EmailOnboardTab>(() =>
      isVerified ? "email-info" : "domain-setup",
    );

    // Derive the effective tab during render instead of syncing in
    // useEffect: when the user's selected tab is hidden by a status change
    // (e.g. they're on "domain-setup" then verify DNS, which removes that
    // tab), fall back to a sensible default without an extra render cycle.
    const activeTab: EmailOnboardTab = visibleTabs.includes(selectedTab)
      ? selectedTab
      : isVerified
        ? "email-info"
        : "domain-setup";

    const tabValue = useMemo(
      () => visibleTabs.indexOf(activeTab),
      [activeTab, visibleTabs],
    );

    const tabLabels = useMemo<Record<EmailOnboardTab, string>>(
      () => ({
        "domain-setup": t("emailOnboard.tab.domainSetup", "寄件網域設定"),
        "dns-verify": t("emailOnboard.tab.dnsVerify", "DNS 驗證"),
        "email-info": t("emailOnboard.tab.emailInfo", "Email 渠道資訊"),
      }),
      [t],
    );

    // Tab disabled states derived from the channel + sender domain state.
    // - "domain-setup": only rendered before verification, always enabled then.
    // - "dns-verify":   enabled once an email channel exists.
    // - "email-info":   only rendered after verification, always enabled then.
    const tabDisabled = useMemo<Record<EmailOnboardTab, boolean>>(() => {
      const status = application?.status;
      return {
        "domain-setup": false,
        "dns-verify": !status,
        "email-info": false,
      };
    }, [application?.status]);

    const handleTabChange = useHandler<TabsProps["onChange"]>(
      (_, newValue: number) => {
        const tab = visibleTabs[newValue];
        if (tab && !tabDisabled[tab]) {
          setSelectedTab(tab);
        }
      },
    );

    const handleJumpToTab = useHandler((tab: EmailOnboardTab) => {
      setSelectedTab(tab);
    });

    const handleApplicationCreated = useHandler((newChannelId: number) => {
      setCreatedChannelId(newChannelId);
      setSelectedTab("dns-verify");
    });

    return (
      <PageLayout>
        <Stack gap="8px">
          <Breadcrumbs aria-label="breadcrumb" separator=">">
            <Link to="/channels" style={{ textDecoration: "none" }}>
              <Typography variant="body" color={theme.colors.staticFgBody}>
                {t("channels.title", "渠道設定")}
              </Typography>
            </Link>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              Email
            </Typography>
          </Breadcrumbs>
          <PageTitle css={css({ marginBottom: 0 })}>
            {t("emailOnboard.pageTitle", "設定 Email")}
          </PageTitle>
        </Stack>

        <InfoBox
          sx={{ maxWidth: "776px" }}
          variant="plain"
          description={
            <Stack direction="column">
              {t(
                "emailOnboard.infoBox.description",
                "請依序完成寄件網域設定、DNS 驗證流程，系統會根據你填入的網域自動產出對應設定。",
              )}
              <ExternalLink
                href="https://crescendolab.zendesk.com/hc/zh-tw/articles/53570915864089"
                trailingIcon={true}
              >
                {t("common.learnMore", "了解更多")}
              </ExternalLink>
            </Stack>
          }
        />

        <Tabs value={tabValue} onChange={handleTabChange}>
          {visibleTabs.map((tab, idx) => (
            <Tab
              key={tab}
              label={tabLabels[tab]}
              disabled={tabDisabled[tab]}
              {...a11yProps(idx)}
            />
          ))}
        </Tabs>

        {visibleTabs.includes("domain-setup") ? (
          <TabPanel
            value={tabValue}
            index={visibleTabs.indexOf("domain-setup")}
          >
            <DomainSetupTab
              onJumpToTab={handleJumpToTab}
              onApplicationCreated={handleApplicationCreated}
              application={application}
            />
          </TabPanel>
        ) : null}
        <TabPanel value={tabValue} index={visibleTabs.indexOf("dns-verify")}>
          <DnsVerifyTab
            onJumpToTab={handleJumpToTab}
            application={application}
          />
        </TabPanel>
        {visibleTabs.includes("email-info") && application?.channelId ? (
          <TabPanel value={tabValue} index={visibleTabs.indexOf("email-info")}>
            <ChannelQueriesContextProviderById
              channelId={application.channelId}
            >
              <EmailInfoTab
                channel={{
                  id: application.channelId,
                  externalChannelId: application.senderDomain,
                }}
              />
            </ChannelQueriesContextProviderById>
          </TabPanel>
        ) : null}
      </PageLayout>
    );
  };
